import { RawUser } from '@prismamedia/one-user';
import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDialog } from '../Dialog';
import { NotificationTypeEnum, useNotification } from '../Notifications';
import { LockedDialog } from './LockedDialog';
import { TimeoutDialog } from './TimeoutDialog';

const REFRESH_BEFORE_SERVER_UNLOCK = 60000; // 1min
const MIN_REFRESH_INTERVAL = 30000; // 30s
const WARNING_TIMER = 60000; // 1min
const THROTTLE_USER_ACTION = 1000; // 1s
const { now } = Date;

export interface LockStatus {
  lockedBy?: Partial<
    Pick<RawUser, 'id' | 'name' | 'email' | 'avatarUrl'>
  > | null;
  lockedAt?: string | null;
}

interface ConcurrentialAccessProps {
  lockStatus: LockStatus | undefined | null;
  unlockTimeout: number;
  onLock: (isInitialLock: boolean) => Promise<any>;
  currentUser: { id: string } | undefined | null;
  unlockLink?: string;
  onNavigateBack?: MouseEventHandler<HTMLButtonElement>;
  clearUnsavedWork?: () => void;
  unlockTimeoutConfigOnServer?: number;
  disableLockForV0?: boolean;
}

export const useConcurrentialAccess = ({
  lockStatus, // if null or undefined (when loading the page for example), concurrential access is disabled
  unlockTimeout: timeout, // cannot be inferior to 'unlockTimeoutConfigOnServer'
  onLock,
  unlockLink,
  currentUser,
  onNavigateBack,
  clearUnsavedWork,
  unlockTimeoutConfigOnServer = 300000, // 5min (should be = to server config)
  disableLockForV0,
}: ConcurrentialAccessProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { pushNotification } = useNotification();
  const { openDialog } = useDialog();
  const [lastLockTryTimestamp, setLastLockTryTimestamp] = useState(0);
  const [lastUserActionTimestamp, setLastUserActionTimestamp] = useState(now());
  const [lockDialogAlreadyDisplayed, setLockDialogAlreadyDisplayed] =
    useState(false);
  const [isInitialLock, setIsInitialLock] = useState(true);
  const unlockTimer = useMemo(
    () =>
      // 'unlockTimer' cannot be inferior to 'unlockTimeoutConfigOnServer'
      timeout > unlockTimeoutConfigOnServer
        ? timeout
        : unlockTimeoutConfigOnServer,
    [timeout, unlockTimeoutConfigOnServer],
  );
  const lockRefreshInterval = useMemo(
    () =>
      Math.max(
        MIN_REFRESH_INTERVAL,
        unlockTimeoutConfigOnServer - REFRESH_BEFORE_SERVER_UNLOCK,
      ),
    [unlockTimeoutConfigOnServer],
  );

  // Init lastLockTryTimestamp
  useEffect(() => {
    if (
      lockStatus?.lockedAt &&
      currentUser &&
      lockStatus.lockedBy?.id === currentUser?.id &&
      !lastLockTryTimestamp
    ) {
      setLastLockTryTimestamp(new Date(lockStatus.lockedAt).getTime());
      setIsInitialLock(false);
    }
  }, [
    currentUser,
    lastLockTryTimestamp,
    lockStatus?.lockedAt,
    lockStatus?.lockedBy?.id,
  ]);

  // If locked by someone else, display LockedDialog
  useEffect(() => {
    if (
      !isDialogOpen &&
      lockStatus?.lockedBy &&
      lockStatus?.lockedBy.id !== currentUser?.id &&
      (!disableLockForV0 || !lockDialogAlreadyDisplayed)
    ) {
      setIsDialogOpen(true);
      setLockDialogAlreadyDisplayed(true);
      openDialog(
        <LockedDialog
          lockStatus={lockStatus}
          unlockLink={unlockLink}
          onNavigateBack={onNavigateBack}
          disableLockForV0={disableLockForV0}
        />,
        {
          disableClose: !disableLockForV0,
          onClose: () => setIsDialogOpen(false),
        },
      );
      clearUnsavedWork?.();
    }
  }, [
    clearUnsavedWork,
    currentUser?.id,
    disableLockForV0,
    isDialogOpen,
    lockStatus,
    lockDialogAlreadyDisplayed,
    onNavigateBack,
    openDialog,
    unlockLink,
  ]);

  const refreshLock = useCallback(async () => {
    setLastLockTryTimestamp(now());
    try {
      await onLock(isInitialLock);
    } catch (e) {
      pushNotification({
        message: (e as Error).message,
        type: NotificationTypeEnum.error,
      });
    }
    setIsInitialLock(false);
  }, [isInitialLock, onLock, pushNotification]);

  const onUserAction = useCallback(() => {
    if (
      !isDialogOpen &&
      now() - lastUserActionTimestamp >= THROTTLE_USER_ACTION
    ) {
      setLastUserActionTimestamp(now());
    }
  }, [isDialogOpen, lastUserActionTimestamp]);

  useEffect(() => {
    addEventListener('mousemove', onUserAction);
    addEventListener('mousedown', onUserAction);
    addEventListener('scroll', onUserAction);
    addEventListener('touchstart', onUserAction);
    addEventListener('keydown', onUserAction);
    return () => {
      removeEventListener('mousemove', onUserAction);
      removeEventListener('mousedown', onUserAction);
      removeEventListener('scroll', onUserAction);
      removeEventListener('touchstart', onUserAction);
      removeEventListener('keydown', onUserAction);
    };
  }, [onUserAction]);

  useEffect(() => {
    let dialogTimeout: any;
    let nextLockRefreshTimeout: any;

    if (
      lockStatus &&
      (!lockStatus.lockedBy || lockStatus.lockedBy.id === currentUser?.id)
    ) {
      const timeSinceLastUserAction = now() - lastUserActionTimestamp;
      const timeSinceLastLockTry = now() - lastLockTryTimestamp;
      const unlockTimeoutValue = unlockTimer - timeSinceLastUserAction;
      const warningTimeoutValue = unlockTimeoutValue - WARNING_TIMER;

      dialogTimeout = setTimeout(
        () => {
          setIsDialogOpen(true);
          openDialog(
            <TimeoutDialog
              lockTimeout={unlockTimer}
              timerStart={Math.min(WARNING_TIMER, unlockTimeoutValue)}
              onNavigateBack={onNavigateBack}
              disableLockForV0={disableLockForV0}
            />,
            {
              onClose: () => {
                setIsDialogOpen(false);
                setLastUserActionTimestamp(now());
              },
            },
          );
        },
        warningTimeoutValue > 0 ? warningTimeoutValue : 0,
      );

      // Example for 'unlockTimeout' = 30 min and 'unlockTimeoutConfigOnServer' = 5 min :
      // After the user stopped doing actions, in order to keep the page "locked",
      // we need to keep refreshing the lock every 4 min ('lockRefreshInterval'), otherwise
      // the server will unlock the page after 5 min of inactivity
      let nextLockRefreshTimeoutValue =
        lockRefreshInterval - timeSinceLastLockTry;

      if (nextLockRefreshTimeoutValue < 0) {
        nextLockRefreshTimeoutValue = 0;
      }

      const diffBetweenLocalAndServerTimeouts =
        unlockTimer - unlockTimeoutConfigOnServer;

      // In case 'diffBetweenLocalAndServerTimeouts' is smaller than 15s (MIN_REFRESH_INTERVAL / 2),
      // we shift the final refresh by (15s - diffBetweenLocalAndServerTimeouts)
      const finalRefreshShiftInCaseDiffIsSmall =
        diffBetweenLocalAndServerTimeouts < MIN_REFRESH_INTERVAL / 2
          ? MIN_REFRESH_INTERVAL / 2 - diffBetweenLocalAndServerTimeouts
          : 0;

      // We need to stop refreshing 5 min before 30 min of inactivity so the server can
      // properly unlock after 30 min of inactivity
      if (
        // Check if the next refresh will fall into the 5 min of our example
        now() + nextLockRefreshTimeoutValue >=
        lastUserActionTimestamp +
          diffBetweenLocalAndServerTimeouts +
          finalRefreshShiftInCaseDiffIsSmall
      ) {
        // The final refresh happen after exactly 30 - 5 = 25 min ('diffBetweenLocalAndServerTimeouts')
        // of inactivity, so the server can precisely unlock after 30 min
        nextLockRefreshTimeoutValue =
          diffBetweenLocalAndServerTimeouts +
          finalRefreshShiftInCaseDiffIsSmall -
          timeSinceLastUserAction;

        // To avoid refreshing faster than 30s ('MIN_REFRESH_INTERVAL'), we check if the final refresh
        // will fall closer than 30s from the last refresh
        if (
          now() + nextLockRefreshTimeoutValue <
          lastLockTryTimestamp + MIN_REFRESH_INTERVAL
        ) {
          // We space the final refresh 30s from the last
          nextLockRefreshTimeoutValue =
            MIN_REFRESH_INTERVAL - timeSinceLastLockTry;

          // If the final refresh will fall after 15s (MIN_REFRESH_INTERVAL / 2) after where it
          // should have fallen in the first place, we cancel it
          if (
            now() + nextLockRefreshTimeoutValue >
            lastUserActionTimestamp +
              diffBetweenLocalAndServerTimeouts +
              MIN_REFRESH_INTERVAL / 2
          ) {
            nextLockRefreshTimeoutValue = -1;
          }
        }
      }

      if (nextLockRefreshTimeoutValue >= 0) {
        nextLockRefreshTimeout = setTimeout(
          () => refreshLock(),
          nextLockRefreshTimeoutValue,
        );
      }
    }

    return () => {
      clearTimeout(dialogTimeout);
      clearTimeout(nextLockRefreshTimeout);
    };
  }, [
    currentUser?.id,
    disableLockForV0,
    lastLockTryTimestamp,
    lastUserActionTimestamp,
    lockRefreshInterval,
    lockStatus,
    onNavigateBack,
    openDialog,
    refreshLock,
    unlockTimeoutConfigOnServer,
    unlockTimer,
  ]);
};
